import { UserSubscription } from '@intelingu/core/interfaces/models/UserSubscriptionModel';
import { SubscriptionStore } from '@intelingu/core/stores/SubscriptionStore';
import { autobind } from 'core-decorators';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Component } from 'react';
import { SubscriptionsApi } from '@intelingu/core/api/SubscriptionsApi';
import Helpers from '@intelingu/core/helpers/Helpers';
import { SubscriptionModel } from '@intelingu/core/interfaces/models/SubscriptionModel';
import classnames from 'classnames';
import { lazyInject } from '@intelingu/core/IoC/container';
import { Button } from 'reactstrap';
import {COMMON_NS} from "@intelingu/core/constants/base.constants";
import withTranslation from "@intelingu/core/utils/withTranslation";

export interface SubscriptionItemProps {
    item: SubscriptionModel;
    t?;
}

@withTranslation(COMMON_NS)
@observer export class SubscriptionItem extends Component<SubscriptionItemProps, any> {

    @lazyInject(SubscriptionsApi) subscriptionsApi: SubscriptionsApi;
    @lazyInject(SubscriptionStore) subscriptionStore: SubscriptionStore;

    @observable isLoading = false;

    @autobind async subscribe(id) {
        if (this.isExpired) {
            window.location = this.currentSubscription.ds24_renew_url as any;
            return;
        }

        if (this.isBought) {
            window.location = this.currentSubscription.ds24_rebilling_stop_url as any;
            return;
        }

        if (this.isCourseBought) {
            window.location = (this.props.item.upgrade_link + this.currentSubscription.ds24_order_id) as any;
            return;
        }

        this.isLoading = true;
        const response = await this.subscriptionsApi.subscribe(id);

        if (response.data.url) {
            window.location = response.data.url as any;
        }
    }

    get isBought() {
        return this.subscriptionStore.subscriptions.filter(
            item => item.id === this.props.item.id && !item.access_stops_on
        ).length > 0;
    }

    get isCourseBought() {
        return !!this.currentSubscription;
    }

    get currentSubscription(): UserSubscription | null {
        const subscriptions = this.subscriptionStore.subscriptions
            .filter(item => item.course_id === this.props.item.course_id && !item.is_trial);

        if (subscriptions.length) {
            return subscriptions[0];
        }

        return null;
    }

    get hasOneTimeProduct(): boolean {
        const subscriptions = this.subscriptionStore.subscriptions
            .filter(item => item.course_id === this.props.item.course_id && !item.interval);

        return subscriptions.length > 0;
    }

    get isExpired() {
        if (this.isBought) {
            if (this.currentSubscription.is_expired) {
                return true;
            }
        }
    }

    get buttonText() {
        const { t } = this.props;

        if (this.isExpired) {
            return t('subscriptionsRenew');
        }

        if (this.isBought) {
            return t('subscriptionsCancel');
        }

        if (this.isCourseBought) {
            return t('subscriptionsPageUpgrade');
        }

        return t('subscriptionsPageSubscribe')
    }

    render() {
        const { item, t } = this.props;

        const title = Helpers.getSubscriptionTitle(item, t);
        const monthPrice = ((item.price / Helpers.getMonthsByPeriod(item.period, item.interval)).toFixed(2) + '')
            .split('.');

        return <div className={classnames('item', { 'primary': item.plan_type === 'bestprice' })} key={item.id}>
            <div className={'title'}>
                { item.plan_type === 'bestprice' && <span>{ t('subscriptionsPageBestPrice') }</span> }
                { item.plan_type === 'bestseller' && <span>{ t('subscriptionsPageBestSeller') }</span> }
                <h4>{ title }</h4>
            </div>

            <div className={'price'}>
                <div className={'amount'}>
                    { monthPrice[0] }<small>.{ monthPrice[1] || '00' } €</small>
                </div>
                <span>{ t('subscriptionsPagePerMonth') }</span>
            </div>

            { !this.hasOneTimeProduct && (!this.isBought || this.isExpired) && <Button
                color={'primary'}
                className={`loading-button`}
                type={'submit'}
                disabled={this.isLoading}
                onClick={() => this.subscribe(item.id)}
            >
                { this.buttonText }
                { this.isLoading && <div className={'loading-overlay'}><div className="lds-dual-ring" /></div> }
            </Button> }

            { !this.hasOneTimeProduct && this.isBought && (
                <div className={'alert alert-success'} style={{ textAlign: 'center', marginBottom: 0, marginTop: 1 }}>{t('subscriptionsPageActiveUntil')} { Helpers.calculateEndDate(this.currentSubscription) }</div>
            ) }

            <span>{ t('subscriptionsPageTotalPrice') }: { item.price } €</span>
        </div>;
    }

}
